import React, { useState, useRef, useCallback } from 'react';
import { useEffect } from 'react';
import { FaVolumeUp, FaSpinner } from 'react-icons/fa';
import api from '../api';
import { useUser } from '../contexts/UserContext';
import { useAudio } from '../contexts/AudioContext';
import { useAsyncTaskPolling } from '../hooks/useAsyncTaskPolling';
import { useTTSStreamSSE } from '../hooks/useSSE';
import { useToast } from '../contexts/ToastContext';
import { playErrorSound } from '../utils/errorSound';

/**
 * Extracts the first markdown header from content
 */
const extractMarkdownHeader = (content) => {
  if (!content) return null;
  const lines = content.split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('#')) {
      // Remove all # characters and trim
      return trimmed.replace(/^#+\s*/, '').trim();
    }
  }
  return null;
};

/**
 * SpeakerIcon component fetches and plays audio for a node, a profile or
 * a saved reference (itemId). Shows loading spinner, play/pause state.
 * `buttonStyle` overrides the button's own style (its spacing, where a
 * row needs a larger tap target).
 */
const SpeakerIcon = ({ nodeId, profileId, itemId, content, isPublic, aiUsage, onTtsGenerated, buttonStyle }) => {
  const { user } = useUser();
  const { loadAudio, loadAudioQueue, updateChapters, appendChunkToQueue, setGeneratingTTS, currentAudio, isPlaying, warmup, waitingForChunks, queueFinishedCount } = useAudio();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [audioChunks, setAudioChunks] = useState(null); // For chunked playback
  const [audioChunkDurations, setAudioChunkDurations] = useState(null); // Server-provided durations
  const [ttsTaskActive, setTtsTaskActive] = useState(false);
  const [sseActive, setSseActive] = useState(false);
  const sseChunkCountRef = useRef(0);
  // Chapters determined for the currently-cached audio (#145). Held in a
  // ref so the in-session replay short-circuits below (cached audioSrc /
  // audioChunks) reuse the same chapter list the player was first loaded
  // with, instead of dropping the dropdown on a second click. Empty for
  // recorded-original or unstructured audio.
  const chaptersRef = useRef([]);
  // Last section_index seen on the SSE stream — used to refresh chapter
  // start times once per chapter as streaming crosses section boundaries.
  const lastSectionRef = useRef(null);

  const isNode = nodeId != null;
  const isItem = !isNode && itemId != null;
  const id = isNode ? nodeId : (isItem ? itemId : profileId);
  const entityType = isNode ? 'node' : (isItem ? 'item' : 'profile');
  const baseUrl = isNode ? `/nodes/${id}`
    : isItem ? `/external/items/${id}` : `/profile/${id}`;

  // Extract header from content if available. The "Node N" form is a
  // fallback only — when the content has a title, show just the title.
  const header = extractMarkdownHeader(content);
  const baseTitle = isNode ? `Node ${id}` : (isItem ? `Reference ${id}` : `Profile ${id}`);
  const fullTitle = header || baseTitle;

  // Chapter list for TTS playback (#145) — empty for unstructured text.
  const fetchChapters = useCallback(async () => {
    try {
      const res = await api.get(`${baseUrl}/tts-chapters`, {
        validateStatus: (s) => s === 200 || s === 404,
      });
      return res.status === 200 ? (res.data.chapters || []) : [];
    } catch (e) {
      return [];
    }
  }, [baseUrl]);

  // SSE streaming for TTS chunks
  const handleChunkReady = useCallback((data) => {
    const chunkUrl = data.audio_url.startsWith('http')
      ? data.audio_url
      : `${process.env.REACT_APP_BACKEND_URL}${data.audio_url}`;
    const chunkDuration = data.duration != null ? data.duration : null;

    sseChunkCountRef.current += 1;
    const section = data.section_index != null ? data.section_index : null;

    if (sseChunkCountRef.current === 1) {
      // First chunk: start playback now, the chapters follow. The queue
      // must exist before the next chunk_ready: a stream whose TTS is
      // already complete sends every chunk at once, and chunks appended
      // while this waited for the chapters were dropped when it loaded.
      setLoading(false);
      lastSectionRef.current = section;
      chaptersRef.current = [];
      loadAudioQueue(
        [chunkUrl],
        { title: fullTitle, id, type: entityType, chapters: [] },
        chunkDuration != null ? [chunkDuration] : null
      );
      fetchChapters().then((chapters) => {
        chaptersRef.current = chapters;
        updateChapters(id, entityType, chapters);
      });
    } else {
      // Subsequent chunks: append to the active queue
      appendChunkToQueue(chunkUrl, chunkDuration);
      // When a chunk from a NEW section arrives, every earlier chunk is
      // already generated — so that chapter's cumulative start time is now
      // final. Refresh the chapter list so the indicator/seek are correct
      // progressively during streaming, one chapter at a time, rather than
      // only at completion (#145). The completion handler re-fetches once
      // more as a final backstop.
      if (section != null && section !== lastSectionRef.current) {
        lastSectionRef.current = section;
        fetchChapters().then((chapters) => {
          chaptersRef.current = chapters;
          updateChapters(id, entityType, chapters);
        });
      }
    }
  }, [fullTitle, id, entityType, loadAudioQueue, appendChunkToQueue, fetchChapters, updateChapters]);

  const handleAllComplete = useCallback((data) => {
    setSseActive(false);
    setGeneratingTTS(false);
    sseChunkCountRef.current = 0;

    // Cache the final TTS URL for future replay
    if (data.tts_url) {
      const finalUrl = data.tts_url.startsWith('http')
        ? data.tts_url
        : `${process.env.REACT_APP_BACKEND_URL}${data.tts_url}`;
      setAudioSrc(finalUrl);
    }
    // The chapters fetched on the first streamed chunk were computed from
    // chunk durations that weren't all generated yet, so later chapters'
    // start times clustered together (wrong indicator + wrong seek). Now
    // that every chunk is generated, re-fetch the final chapters and swap
    // them into both the live player and the replay cache (#145).
    fetchChapters().then((chapters) => {
      chaptersRef.current = chapters;
      updateChapters(id, entityType, chapters);
    });
    // Fresh generation completes via this SSE path (the tts-status poll is
    // only a fallback), so tell the parent the entry now has TTS — this is
    // what makes the edit "regenerate audio?" prompt (#66) fire without a
    // page refresh.
    if (onTtsGenerated) onTtsGenerated();
  }, [setGeneratingTTS, onTtsGenerated, fetchChapters, updateChapters, id, entityType]);

  // A failed generation is announced as a failed voice reply is (#504):
  // the error sound, here with a toast, after the audio that did arrive
  // has played. Holds { finishedCount } while it waits for that audio.
  const failureAfterQueueRef = useRef(null);
  const announceTtsFailure = useCallback((partial) => {
    playErrorSound();
    addToast(partial
      ? "Loore couldn't read the rest aloud. Tap the speaker to try again from the start."
      : "Loore couldn't read this aloud. Tap the speaker to try again.", 6000);
  }, [addToast]);

  // The server's `error` event: generation failed. Turning the stream off
  // ends the reconnects (the server refuses a failed entry's stream); the
  // chunks already queued keep playing.
  const handleStreamError = useCallback(() => {
    const partial = sseChunkCountRef.current > 0;
    setSseActive(false);
    setGeneratingTTS(false);
    setLoading(false);
    sseChunkCountRef.current = 0;
    const ours = currentAudio && currentAudio.id === id && currentAudio.type === entityType;
    if (!partial || (ours && waitingForChunks)) {
      announceTtsFailure(partial);
    } else if (ours) {
      failureAfterQueueRef.current = { finishedCount: queueFinishedCount };
    }
    // Otherwise other audio has replaced this one: nothing to announce.
  }, [setGeneratingTTS, currentAudio, id, entityType, waitingForChunks, queueFinishedCount, announceTtsFailure]);

  useEffect(() => {
    const waiting = failureAfterQueueRef.current;
    if (!waiting) return;
    const ours = currentAudio && currentAudio.id === id && currentAudio.type === entityType;
    if (!ours) {
      failureAfterQueueRef.current = null;
    } else if (queueFinishedCount !== waiting.finishedCount) {
      failureAfterQueueRef.current = null;
      announceTtsFailure(true);
    }
  }, [currentAudio, id, entityType, queueFinishedCount, announceTtsFailure]);

  const { disconnect: disconnectSSE } = useTTSStreamSSE(id, {
    enabled: sseActive,
    entityType,
    onChunkReady: handleChunkReady,
    onAllComplete: handleAllComplete,
    onError: handleStreamError,
  });

  // TTS generation polling - fallback when SSE isn't used (profiles, or SSE failure)
  const {
    status: ttsStatus,
    data: ttsData,
    error: ttsError
  } = useAsyncTaskPolling(
    ttsTaskActive ? `${baseUrl}/tts-status` : null,
    { enabled: ttsTaskActive }
  );

  // Reset cached audio when the node/profile changes OR its content is
  // edited. Without the `content` dependency, editing a node and choosing
  // "regenerate" cleared the server-side audio but this component kept its
  // cached audioSrc/chunks, so the next play replayed the stale clip
  // instead of regenerating (#66). Content only changes on a real edit, so
  // this correctly invalidates the cache exactly then.
  useEffect(() => {
    setAudioSrc(null);
    setAudioChunks(null);
    setAudioChunkDurations(null);
    setLoading(false);
    setTtsTaskActive(false);
    setSseActive(false);
    sseChunkCountRef.current = 0;
    chaptersRef.current = [];
    lastSectionRef.current = null;
    failureAfterQueueRef.current = null;
  }, [nodeId, profileId, itemId, content]);

  // Clean up SSE on unmount
  useEffect(() => {
    return () => {
      if (sseActive) {
        disconnectSSE();
        setGeneratingTTS(false);
      }
    };
  }, [sseActive, disconnectSSE, setGeneratingTTS]);

  // Handle TTS completion (polling fallback)
  useEffect(() => {
    if (ttsStatus === 'completed' && ttsData) {
      const ttsUrl = (ttsData.node || ttsData.item || ttsData.profile || {}).audio_tts_url;
      if (ttsUrl) {
        // Build absolute URL
        const srcUrl = ttsUrl.startsWith('http')
          ? ttsUrl
          : `${process.env.REACT_APP_BACKEND_URL}${ttsUrl}`;
        setAudioSrc(srcUrl);
        // Add a small delay to ensure file is available on server before loading
        setTimeout(() => {
          loadAudio({ url: srcUrl, title: fullTitle, id, type: entityType });
        }, 500);
        // Tell the parent this entry now has generated TTS, so its cached
        // node/profile object updates without a page refresh — otherwise the
        // edit flow's "regenerate audio?" prompt (#66) wouldn't fire until
        // the page is reloaded.
        if (onTtsGenerated) onTtsGenerated();
      }
      setTtsTaskActive(false);
      setLoading(false);
    } else if (ttsStatus === 'failed') {
      console.error('TTS generation failed:', ttsError);
      setTtsTaskActive(false);
      setLoading(false);
    }
  }, [ttsStatus, ttsData, ttsError, entityType, id, loadAudio, fullTitle, onTtsGenerated]);

  // Show for voice-mode users, or for any authenticated user on public posts
  if (!user || (!user.voice_mode_enabled && !isPublic)) {
    return null;
  }

  // New speech sends the text to a model: off for an entry, a reply or a
  // profile version whose AI usage is 'none' (the server refuses it too).
  const noAiAccess = (nodeId != null || profileId != null) && aiUsage === 'none';

  const handleClick = async () => {
    if (noAiAccess || loading || ttsTaskActive || sseActive) return;
    warmup(); // Unlock audio on Safari during user gesture

    try {
      // If we already have audio chunks cached, play them (carry the
      // chapters from the first load so a same-session replay keeps the
      // dropdown — #145)
      if (audioChunks && audioChunks.length > 0) {
        await loadAudioQueue(audioChunks, { title: fullTitle, id, type: entityType, chapters: chaptersRef.current }, audioChunkDurations);
        return;
      }

      // If we already have a single audio source, play it (same: preserve
      // the cached chapters on replay — #145)
      if (audioSrc) {
        await loadAudio({ url: audioSrc, title: fullTitle, id, type: entityType, chapters: chaptersRef.current });
        return;
      }

      setLoading(true);

      // Attempt to fetch existing audio URLs
      let original_url = null;
      let tts_url = null;
      let has_audio_chunks = false;
      try {
        const res = await api.get(`${baseUrl}/audio`, {
          validateStatus: (status) => status === 200 || status === 404
        });
        if (res.status === 200) {
          original_url = res.data.original_url;
          tts_url = res.data.tts_url;
          has_audio_chunks = res.data.has_audio_chunks || false;
        }
        // If status is 404, both remain null (no audio exists yet)
      } catch (getErr) {
        // Handle any other errors
        console.error('Error checking for audio:', getErr);
      }

      // Prefer original recording (including audio chunks) over TTS
      // If audio chunks exist, try loading them before falling back to TTS
      if (has_audio_chunks && isNode) {
        try {
          const chunksRes = await api.get(`${baseUrl}/audio-chunks`, {
            validateStatus: (status) => status === 200 || status === 404
          });
          if (chunksRes.status === 200 && chunksRes.data.chunks?.length > 0) {
            const chunksData = chunksRes.data.chunks;
            const chunkUrls = chunksData.map(chunk => {
              const url = typeof chunk === 'string' ? chunk : chunk.url;
              return url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
            });
            const chunkDurations = chunksData.map(chunk =>
              typeof chunk === 'object' && chunk.duration != null ? chunk.duration : null
            );
            setAudioChunks(chunkUrls);
            setAudioChunkDurations(chunkDurations);
            setLoading(false);
            await loadAudioQueue(chunkUrls, { title: fullTitle, id, type: 'node' }, chunkDurations);
            return;
          }
        } catch (chunksErr) {
          console.error('Error loading audio chunks:', chunksErr);
        }
      }

      let urlPath = original_url || tts_url;

      if (!urlPath && isNode) {
        // No single audio file - check for audio chunks (streaming transcription nodes)
        try {
          const chunksRes = await api.get(`${baseUrl}/audio-chunks`, {
            validateStatus: (status) => status === 200 || status === 404
          });
          if (chunksRes.status === 200 && chunksRes.data.chunks?.length > 0) {
            // Backend returns [{url, duration}, ...] with accurate durations from ffprobe
            const chunksData = chunksRes.data.chunks;
            const chunkUrls = chunksData.map(chunk => {
              const url = typeof chunk === 'string' ? chunk : chunk.url;
              return url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
            });
            // Extract server-provided durations (accurate via ffprobe)
            const chunkDurations = chunksData.map(chunk =>
              typeof chunk === 'object' && chunk.duration != null ? chunk.duration : null
            );
            setAudioChunks(chunkUrls);
            setAudioChunkDurations(chunkDurations);
            setLoading(false);
            await loadAudioQueue(chunkUrls, { title: fullTitle, id, type: 'node' }, chunkDurations);
            return;
          }
        } catch (chunksErr) {
          console.error('Error checking for audio chunks:', chunksErr);
        }
      }

      if (!urlPath) {
        // No audio exists — only voice-mode users may generate TTS
        if (!user.voice_mode_enabled) {
          setLoading(false);
          return;
        }
        // Start async TTS generation (an earlier failure's announcement
        // no longer applies)
        failureAfterQueueRef.current = null;
        await api.post(`${baseUrl}/tts`);

        // Use SSE for streaming playback (nodes and profiles)
        sseChunkCountRef.current = 0;
        setSseActive(true);
        setGeneratingTTS(true);
        // loading stays true until first chunk arrives
        return;
      }

      const srcUrl = urlPath.startsWith('http')
        ? urlPath
        : `${process.env.REACT_APP_BACKEND_URL}${urlPath}`;
      setAudioSrc(srcUrl);

      // Load audio into global player (chapters only for TTS audio —
      // recorded-original playback has no section structure, #145)
      const chapters = (!original_url && tts_url) ? await fetchChapters() : [];
      chaptersRef.current = chapters;
      await loadAudio({ url: srcUrl, title: fullTitle, id, type: entityType, chapters });
      setLoading(false);
    } catch (err) {
      console.error('Error playing audio:', err);
      setLoading(false);
      setTtsTaskActive(false);
      setSseActive(false);
      setGeneratingTTS(false);
    }
  };

  // Check if this is the currently playing audio
  const isCurrentlyPlaying = currentAudio &&
    currentAudio.id === id &&
    currentAudio.type === entityType &&
    isPlaying;

  const isActive = loading || ttsTaskActive || sseActive;

  return (
    <button onClick={handleClick} title={noAiAccess ? 'TTS disabled — No AI access' : isActive ? 'Generating audio...' : 'Play audio'}
        disabled={noAiAccess}
        style={{ background: 'none', border: 'none', cursor: noAiAccess ? 'not-allowed' : 'pointer', padding: 0, marginLeft: '8px', opacity: noAiAccess ? 0.35 : 1, ...buttonStyle }}>
      {isActive ? <FaSpinner className="spin" /> : <FaVolumeUp color={isCurrentlyPlaying ? 'var(--accent)' : 'inherit'} />}
    </button>
  );
};

export default SpeakerIcon;
